import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { 
    getFirestore, 
    collection, 
    onSnapshot, 
    addDoc, 
    deleteDoc, 
    doc, 
    updateDoc, 
    query,
    setDoc
} from 'firebase/firestore';

// --- Iconos SVG como componentes de React ---
const TrashIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>);
const PlusIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>);
const UtensilsIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3z"/></svg>);
const ShoppingCartIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>);
const UsersIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>);
const CalendarIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>);
const ChevronLeftIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><polyline points="15 18 9 12 15 6"></polyline></svg>);
const ChevronRightIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><polyline points="9 18 15 12 9 6"></polyline></svg>);


// --- Componente para la Lista de la Compra ---
const ShoppingList = ({ items, onAddItem, onToggleItem, onDeleteItem }) => {
    const [newItem, setNewItem] = useState('');
    const handleSubmit = (e) => { e.preventDefault(); if (newItem.trim()) { onAddItem(newItem.trim()); setNewItem(''); } };
    return (
        <div className="bg-white p-6 rounded-lg shadow-lg animate-fade-in">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Lista de la Compra</h2>
            <form onSubmit={handleSubmit} className="flex gap-2 mb-4">
                <input type="text" value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Añadir producto..." className="flex-grow p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"/>
                <button type="submit" className="bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center shadow-md hover:shadow-lg"><PlusIcon /></button>
            </form>
            <ul className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {items.length > 0 ? items.map(item => (
                    <li key={item.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg shadow-sm hover:bg-gray-100 transition-colors">
                        <div className="flex items-center cursor-pointer" onClick={() => onToggleItem(item.id, !item.completed)}>
                            <input type="checkbox" checked={item.completed} readOnly className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"/>
                            <span className={`text-gray-700 ${item.completed ? 'line-through text-gray-400' : ''}`}>{item.text}</span>
                        </div>
                        <button onClick={() => onDeleteItem(item.id)} className="text-gray-400 hover:text-red-500 transition-colors"><TrashIcon /></button>
                    </li>
                )) : (<p className="text-gray-500 text-center py-4">Tu lista de la compra está vacía.</p>)}
            </ul>
        </div>
    );
};

// --- Componente para gestionar Personas ---
const PeopleManager = ({ people, onAddPerson, onDeletePerson }) => {
    const [newName, setNewName] = useState('');
    const handleSubmit = (e) => { e.preventDefault(); if (newName.trim()) { onAddPerson(newName.trim()); setNewName(''); } };
    return (
        <div className="bg-white p-6 rounded-lg shadow-lg animate-fade-in">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestionar Personas</h2>
            <form onSubmit={handleSubmit} className="flex gap-2 mb-4">
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre de la persona..." className="flex-grow p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow"/>
                <button type="submit" className="bg-purple-500 text-white p-3 rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center shadow-md hover:shadow-lg"><PlusIcon /></button>
            </form>
            <ul className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {people.length > 0 ? people.map(person => (
                    <li key={person.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg shadow-sm hover:bg-gray-100 transition-colors">
                        <span className="text-gray-700">{person.name}</span>
                        <button onClick={() => onDeletePerson(person.id)} className="text-gray-400 hover:text-red-500 transition-colors"><TrashIcon /></button>
                    </li>
                )) : (<p className="text-gray-500 text-center py-4">Aún no has añadido a nadie.</p>)}
            </ul>
        </div>
    );
};

// --- Componente para el Planificador de Menús ---
const MenuPlanner = ({ menu, people, onAddMeal, onDeleteMeal, selectedDate, setSelectedDate }) => {
    const [person, setPerson] = useState('');
    const [dish, setDish] = useState('');
    const [mealType, setMealType] = useState('comida');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (person.trim() && dish.trim()) {
            onAddMeal({ id: crypto.randomUUID(), person: person.trim(), dish: dish.trim(), type: mealType });
            setDish('');
        }
    };
    
    const changeDay = (amount) => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + amount);
        setSelectedDate(newDate);
    };

    const comidas = menu.meals.filter(m => m.type === 'comida');
    const cenas = menu.meals.filter(m => m.type === 'cena');

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
                 <h2 className="text-2xl font-bold text-gray-800">Planificador de Menú</h2>
                 <div className="flex items-center gap-2 mt-2 sm:mt-0">
                    <button onClick={() => changeDay(-1)} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ChevronLeftIcon/></button>
                     <input type="date" value={selectedDate.toISOString().split('T')[0]} 
                        onChange={(e) => setSelectedDate(new Date(e.target.value.replace(/-/g, '/')))}
                        className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                    />
                    <button onClick={() => changeDay(1)} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ChevronRightIcon/></button>
                 </div>
            </div>
            <p className="text-sm text-gray-500 mb-4 text-center sm:text-left">{selectedDate.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <select value={person} onChange={(e) => setPerson(e.target.value)} className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"><option value="">Seleccionar persona</option>{people.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}</select>
                <input type="text" value={dish} onChange={(e) => setDish(e.target.value)} placeholder="Plato" className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"/>
                <select value={mealType} onChange={(e) => setMealType(e.target.value)} className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"><option value="comida">Comida</option><option value="cena">Cena</option></select>
                <button type="submit" className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition-colors shadow-md hover:shadow-lg flex items-center justify-center"><PlusIcon /> <span className="ml-1">Añadir</span></button>
            </form>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-700 border-b-2 border-green-200 pb-1">Comidas</h3>
                    <ul className="space-y-2">{comidas.length > 0 ? comidas.map((meal) => (<li key={meal.id} className="flex justify-between items-center bg-green-50 p-3 rounded-lg"><div><span className="font-bold text-green-800">{meal.person}:</span><span className="text-gray-600 ml-2">{meal.dish}</span></div><button onClick={() => onDeleteMeal(meal.id)} className="text-gray-400 hover:text-red-500 transition-colors"><TrashIcon /></button></li>)) : <p className="text-gray-500">No hay comidas planificadas.</p>}</ul>
                </div>
                <div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-700 border-b-2 border-blue-200 pb-1">Cenas</h3>
                    <ul className="space-y-2">{cenas.length > 0 ? cenas.map((meal) => (<li key={meal.id} className="flex justify-between items-center bg-blue-50 p-3 rounded-lg"><div><span className="font-bold text-blue-800">{meal.person}:</span><span className="text-gray-600 ml-2">{meal.dish}</span></div><button onClick={() => onDeleteMeal(meal.id)} className="text-gray-400 hover:text-red-500 transition-colors"><TrashIcon /></button></li>)) : <p className="text-gray-500">No hay cenas planificadas.</p>}</ul>
                </div>
            </div>
        </div>
    );
};

// --- Componente para la Vista de Agenda Semanal ---
const AgendaView = ({ currentDate, setCurrentDate, allMenus, onSelectDate }) => {
    const changeWeek = (amount) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + (7 * amount));
            return newDate;
        });
    };

    const dayOfWeek = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1; 
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - dayOfWeek);

    const weekDays = Array.from({ length: 7 }, (_, i) => {
        const day = new Date(startOfWeek);
        day.setDate(startOfWeek.getDate() + i);
        return day;
    });

    const endOfWeek = weekDays[6];

    // Helper function to group meals by person
    const groupMealsByPerson = (meals) => {
        return meals.reduce((acc, meal) => {
            if (!acc[meal.person]) {
                acc[meal.person] = { comida: [], cena: [] };
            }
            acc[meal.person][meal.type].push(meal);
            return acc;
        }, {});
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => changeWeek(-1)} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ChevronLeftIcon/></button>
                <h3 className="text-lg font-semibold text-gray-700 text-center">
                    {startOfWeek.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} - {endOfWeek.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>
                <button onClick={() => changeWeek(1)} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ChevronRightIcon/></button>
            </div>
            <div className="space-y-4">
                {weekDays.map(day => {
                    const dateStr = day.toISOString().split('T')[0];
                    const menu = allMenus[dateStr];
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    const mealsByPerson = menu ? groupMealsByPerson(menu.meals) : {};

                    return (
                        <div key={dateStr} onClick={() => onSelectDate(day)} className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${isToday ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-100 hover:shadow-md'}`}>
                            <h4 className={`font-bold text-gray-800 capitalize ${isToday ? 'text-blue-600' : ''}`}>{day.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' })}</h4>
                            {!menu || menu.meals.length === 0 ? (
                                <p className="text-sm text-gray-500 mt-2">No hay menús planificados.</p>
                            ) : (
                                <div className="mt-3 space-y-3">
                                    {Object.entries(mealsByPerson).map(([person, meals]) => (
                                        <div key={person} className="border-l-4 border-indigo-400 pl-3">
                                            <h5 className="font-semibold text-indigo-800 mb-2">{person}</h5>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                                <div>
                                                    <span className="font-medium text-green-700">Comida: </span>
                                                    {meals.comida.length > 0 ? (
                                                        <span className="text-gray-600">{meals.comida.map(m => m.dish).join(', ')}</span>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <span className="font-medium text-blue-700">Cena: </span>
                                                    {meals.cena.length > 0 ? (
                                                        <span className="text-gray-600">{meals.cena.map(m => m.dish).join(', ')}</span>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- Componente para la Vista de Calendario ---
const CalendarView = ({ allMenus, onSelectDate }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewType, setViewType] = useState('week'); // 'month' or 'week'

    const changeMonth = (amount) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() + amount);
            return newDate;
        });
    };

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Lunes=0, Domingo=6

    const days = Array.from({ length: startDayOfWeek + daysInMonth }, (_, i) => {
        if (i < startDayOfWeek) return null;
        return new Date(year, month, i - startDayOfWeek + 1);
    });
    
    const weekDays = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg animate-fade-in">
             <div className="flex justify-center mb-4 border border-gray-200 rounded-lg p-1 bg-gray-50">
                <button onClick={() => setViewType('week')} className={`w-full py-2 rounded-md transition-colors text-sm font-semibold ${viewType === 'week' ? 'bg-indigo-500 text-white shadow' : 'text-gray-600 hover:bg-indigo-100'}`}>Vista Semanal</button>
                <button onClick={() => setViewType('month')} className={`w-full py-2 rounded-md transition-colors text-sm font-semibold ${viewType === 'month' ? 'bg-indigo-500 text-white shadow' : 'text-gray-600 hover:bg-indigo-100'}`}>Vista Mensual</button>
            </div>

            {viewType === 'month' ? (
                <>
                    <div className="flex justify-between items-center mb-4">
                        <button onClick={() => changeMonth(-1)} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ChevronLeftIcon/></button>
                        <h2 className="text-2xl font-bold text-gray-800 capitalize">{currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}</h2>
                        <button onClick={() => changeMonth(1)} className="p-2 rounded-full hover:bg-gray-200 transition-colors"><ChevronRightIcon/></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center">
                        {weekDays.map(day => <div key={day} className="font-bold text-gray-600 p-2">{day}</div>)}
                        {days.map((day, index) => {
                            if (!day) return <div key={`empty-${index}`}></div>;
                            const dateStr = day.toISOString().split('T')[0];
                            const menu = allMenus[dateStr];
                            const isToday = dateStr === new Date().toISOString().split('T')[0];

                            return (
                                <div key={dateStr} onClick={() => onSelectDate(day)} className={`p-2 border rounded-lg cursor-pointer transition-colors ${isToday ? 'bg-blue-100' : 'hover:bg-gray-100'}`}>
                                    <span className={`flex items-center justify-center h-8 w-8 mx-auto rounded-full ${isToday ? 'bg-blue-500 text-white' : ''}`}>{day.getDate()}</span>
                                    {menu && menu.meals.length > 0 && 
                                        <div className="mt-1 text-xs flex justify-center items-center gap-1 flex-wrap">
                                            {menu.meals.some(m => m.type === 'comida') && <div className="w-2 h-2 bg-green-500 rounded-full" title="Comida planificada"></div>}
                                            {menu.meals.some(m => m.type === 'cena') && <div className="w-2 h-2 bg-blue-500 rounded-full" title="Cena planificada"></div>}
                                        </div>
                                    }
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <AgendaView currentDate={currentDate} setCurrentDate={setCurrentDate} allMenus={allMenus} onSelectDate={onSelectDate} />
            )}
        </div>
    );
};


// --- Componente Principal de la App ---
export default function App() {
    const [view, setView] = useState('calendar');
    const [shoppingList, setShoppingList] = useState([]);
    const [people, setPeople] = useState([]);
    const [allMenus, setAllMenus] = useState({});
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [db, setDb] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);

    const appId = (process.env.REACT_APP_ID || 'default-app-id').replace(/\//g, '_');

    useEffect(() => {
        const initializeFirebase = async () => {
            try {

                let firebaseConfig = null;
                try {
                    firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG || 'null');
                } catch (error) {
                    console.error("Error parsing Firebase config:", error);
                    firebaseConfig = null;
                }
        
                if (!firebaseConfig) {
                    console.error("La configuración de Firebase no está definida. Asegúrate de crear un archivo .env y añadir REACT_APP_FIREBASE_CONFIG.");
                    return;
                }
                
                console.log("Initializing Firebase with config:", firebaseConfig);
                
                const app = initializeApp(firebaseConfig);
                const firestore = getFirestore(app);
                const authInstance = getAuth(app);
                
                setDb(firestore);
                
                // Test Firebase Auth availability
                try {
                    await signInAnonymously(authInstance);
                    console.log("Firebase Auth is working correctly");
                } catch (authError) {
                    console.error("Firebase Auth error:", authError);
                    setAuthError(authError.message);
                    
                    // Fallback: use a local user ID
                    const fallbackUserId = localStorage.getItem('fallback-user-id') || 'local-user-' + Math.random().toString(36).substr(2, 9);
                    localStorage.setItem('fallback-user-id', fallbackUserId);
                    console.log("Using fallback user ID:", fallbackUserId);
                    setLoading(false);
                    return;
                }
                
                onAuthStateChanged(authInstance, async (user) => {
                    console.log("Auth state changed:", user?.uid);
                    if (user) { 
                        setAuthError(null);
                    } else {
                        console.log("No user signed in, attempting anonymous sign-in...");
                        try {
                            const result = await signInAnonymously(authInstance);
                            console.log("Anonymous sign-in successful:", result.user.uid);
                            setAuthError(null);
                        } catch (error) {
                            console.error("Error signing in anonymously:", error);
                            setAuthError(error.message);
                            
                            // Fallback: use a local user ID
                            const fallbackUserId = localStorage.getItem('fallback-user-id') || 'local-user-' + Math.random().toString(36).substr(2, 9);
                            localStorage.setItem('fallback-user-id', fallbackUserId);
                            console.log("Using fallback user ID:", fallbackUserId);
                        }
                    }
                    setLoading(false);
                });
                
            } catch (error) { 
                console.error("Error initializing Firebase:", error);
                setAuthError(error.message);
                
                // Fallback: use localStorage for offline mode
                const fallbackUserId = localStorage.getItem('fallback-user-id') || 'local-user-' + Math.random().toString(36).substr(2, 9);
                localStorage.setItem('fallback-user-id', fallbackUserId);
                console.log("Firebase failed, using fallback mode with user ID:", fallbackUserId);
                setLoading(false);
            }
        };
        
        initializeFirebase();
    }, []);

    useEffect(() => {
        if (!db) return;
        setLoading(true);
        const unsubscribers = [];

        const collectionsToSync = { listaCompra: setShoppingList, personas: setPeople };
        Object.entries(collectionsToSync).forEach(([coll, setter]) => {
            const path = `artifacts/${appId}/${coll}`;
            const q = query(collection(db, path));
            unsubscribers.push(onSnapshot(q, (snapshot) => {
                setter(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            }, (err) => console.error(`Error en ${coll}:`, err)));
        });

        const menusPath = `artifacts/${appId}/menus`;
        const qMenus = query(collection(db, menusPath));
        unsubscribers.push(onSnapshot(qMenus, (snapshot) => {
            const menusByDate = {};
            snapshot.forEach(doc => { menusByDate[doc.id] = doc.data(); });
            setAllMenus(menusByDate);
        }, (err) => console.error("Error en menús:", err)));

        setLoading(false);
        return () => unsubscribers.forEach(unsub => unsub());
    }, [db, appId]);
    
    const menuForSelectedDate = allMenus[selectedDate.toISOString().split('T')[0]] || { meals: [] };

    const handleAddItem = async (text) => { if (db) await addDoc(collection(db, `artifacts/${appId}/listaCompra`), { text, completed: false }); };
    const handleToggleItem = async (id, completed) => { if (db) await updateDoc(doc(db, `artifacts/${appId}/listaCompra`, id), { completed }); };
    const handleDeleteItem = async (id) => { if (db) await deleteDoc(doc(db, `artifacts/${appId}/listaCompra`, id)); };
    const handleAddPerson = async (name) => { if (db) await addDoc(collection(db, `artifacts/${appId}/personas`), { name }); };
    const handleDeletePerson = async (id) => { if (db) await deleteDoc(doc(db, `artifacts/${appId}/personas`, id)); };
    
    const handleAddMeal = async (newMeal) => {
        if (!db) return;
        const dateStr = selectedDate.toISOString().split('T')[0];
        const newMeals = [...menuForSelectedDate.meals, newMeal];
        await setDoc(doc(db, `artifacts/${appId}/menus`, dateStr), { meals: newMeals });
    };

    const handleDeleteMeal = async (mealId) => {
        if (!db) return;
        const dateStr = selectedDate.toISOString().split('T')[0];
        const newMeals = menuForSelectedDate.meals.filter(m => m.id !== mealId);
        await setDoc(doc(db, `artifacts/${appId}/menus`, dateStr), { meals: newMeals });
    };
    
    const handleSelectDateFromCalendar = (date) => {
        setSelectedDate(date);
        setView('menu');
    }

    if (loading) { 
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-lg text-gray-600 mb-2">Cargando y conectando...</p>
                    {authError && (
                        <p className="text-sm text-orange-600">
                            Problema de conexión: {authError}
                        </p>
                    )}
                </div>
            </div>
        ); 
    }

    return (
        <div className="bg-gray-100 min-h-screen font-sans">
            <style>{`.animate-fade-in { animation: fade-in 0.5s ease-out forwards; } @keyframes fade-in { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            <div className="container mx-auto p-4 sm:p-6 md:p-8 max-w-4xl">
                <header className="text-center mb-8">
                    <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-800 tracking-tight">MealMeat</h1>
                    <p className="text-gray-500 mt-2">Organiza tus compras y tus menús fácilmente.</p>
                    {authError && !db && (
                        <div className="mt-2 p-2 bg-orange-100 border border-orange-300 rounded-md">
                            <p className="text-sm text-orange-700">
                                Modo sin conexión: Los datos se guardan localmente
                            </p>
                        </div>
                    )}
                </header>
                <div className="bg-white rounded-lg shadow-md p-2 flex flex-col sm:flex-row justify-center gap-2 mb-8">
                    <button onClick={() => setView('calendar')} className={`w-full py-3 px-4 rounded-md text-sm sm:text-base font-semibold transition-all duration-300 flex items-center justify-center ${view === 'calendar' ? 'bg-indigo-500 text-white shadow-lg' : 'bg-transparent text-gray-600 hover:bg-indigo-50'}`}><CalendarIcon/><span>Calendario</span></button>
                    <button onClick={() => setView('menu')} className={`w-full py-3 px-4 rounded-md text-sm sm:text-base font-semibold transition-all duration-300 flex items-center justify-center ${view === 'menu' ? 'bg-green-500 text-white shadow-lg' : 'bg-transparent text-gray-600 hover:bg-green-50'}`}><UtensilsIcon/><span>Planificar Menú</span></button>
                    <button onClick={() => setView('list')} className={`w-full py-3 px-4 rounded-md text-sm sm:text-base font-semibold transition-all duration-300 flex items-center justify-center ${view === 'list' ? 'bg-blue-500 text-white shadow-lg' : 'bg-transparent text-gray-600 hover:bg-blue-50'}`}><ShoppingCartIcon/><span>Lista de Compra</span></button>
                    <button onClick={() => setView('people')} className={`w-full py-3 px-4 rounded-md text-sm sm:text-base font-semibold transition-all duration-300 flex items-center justify-center ${view === 'people' ? 'bg-purple-500 text-white shadow-lg' : 'bg-transparent text-gray-600 hover:bg-purple-50'}`}><UsersIcon/><span>Gestionar Personas</span></button>
                </div>
                <main>
                    {view === 'list' && <ShoppingList items={shoppingList} onAddItem={handleAddItem} onToggleItem={handleToggleItem} onDeleteItem={handleDeleteItem} />}
                    {view === 'menu' && <MenuPlanner menu={menuForSelectedDate} people={people} onAddMeal={handleAddMeal} onDeleteMeal={handleDeleteMeal} selectedDate={selectedDate} setSelectedDate={setSelectedDate} />}
                    {view === 'people' && <PeopleManager people={people} onAddPerson={handleAddPerson} onDeletePerson={handleDeletePerson} />}
                    {view === 'calendar' && <CalendarView allMenus={allMenus} onSelectDate={handleSelectDateFromCalendar} />}
                </main>
            </div>
        </div>
    );
}